export const createCrystalBackgroundSVG = () => {
  // Extract SVG creation logic here
  const createQuartzLattice = () => {
    const elements = [];

    // Main hexagonal crystal structures
    for (let i = 0; i < 5; i++) {
      const x = 120 + i * 220;
      const y = 80;
      const size = 35;

      // Primary hexagon
      elements.push(`
        <polygon points='
          ${x},${y} 
          ${x + size},${y - size * 0.866} 
          ${x + size * 2},${y} 
          ${x + size * 2},${y + size} 
          ${x + size},${y + size + size * 0.866} 
          ${x},${y + size}'
          fill='none' 
          stroke='#ffffff' 
          stroke-width='1.5' 
          stroke-opacity='0.2'
        />
      `);

      // Internal crystal facets
      elements.push(`
        <line x1='${x + size}' y1='${y - size * 0.866}' 
              x2='${x + size}' y2='${y + size + size * 0.866}' 
              stroke='#ffffff' stroke-width='0.8' stroke-opacity='0.15'/>
        <line x1='${x}' y1='${y}' 
              x2='${x + size * 2}' y2='${y + size}' 
              stroke='#ffffff' stroke-width='0.8' stroke-opacity='0.15'/>
        <line x1='${x}' y1='${y + size}' 
              x2='${x + size * 2}' y2='${y}' 
              stroke='#ffffff' stroke-width='0.8' stroke-opacity='0.15'/>
      `);
    }

    // Secondary smaller crystals
    for (let i = 0; i < 6; i++) {
      const x = 50 + i * 190;
      const y = 180;
      const size = 20;

      elements.push(`
        <polygon points='
          ${x},${y} 
          ${x + size},${y - size * 0.866} 
          ${x + size * 2},${y} 
          ${x + size * 2},${y + size} 
          ${x + size},${y + size + size * 0.866} 
          ${x},${y + size}'
          fill='none' 
          stroke='#a3b2f2' 
          stroke-width='1' 
          stroke-opacity='0.25'
        />
      `);
    }

    // Crystal connection lines
    for (let i = 0; i < 4; i++) {
      const x1 = 230 + i * 220;
      const y1 = 80 + 35;
      const x2 = 150 + (i + 1) * 190;
      const y2 = 180;

      elements.push(`
        <line x1='${x1}' y1='${y1}' x2='${x2}' y2='${y2}' 
              stroke='#8fa3e6' stroke-width='0.6' stroke-opacity='0.2'
              stroke-dasharray='3,2'/>
      `);
    }

    return elements.join("");
  };

  // Add atomic lattice points
  const createAtomicLattice = () => {
    const points = [];

    // Regular lattice grid
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 15; col++) {
        const x = 80 + col * 80;
        const y = 40 + row * 35;
        const opacity = 0.1 + Math.sin(row + col) * 0.05;

        points.push(`
          <circle cx='${x}' cy='${y}' r='1.2' 
                  fill='#ffffff' opacity='${opacity}'/>
        `);

        // Connect adjacent points
        if (col < 14) {
          points.push(`
            <line x1='${x}' y1='${y}' x2='${x + 80}' y2='${y}' 
                  stroke='#ffffff' stroke-width='0.3' opacity='0.08'/>
          `);
        }
        if (row < 7) {
          points.push(`
            <line x1='${x}' y1='${y}' x2='${x}' y2='${y + 35}' 
                  stroke='#ffffff' stroke-width='0.3' opacity='0.08'/>
          `);
        }
      }
    }

    return points.join("");
  };

  // Create crystal growth patterns
  const createCrystalGrowth = () => {
    const patterns = [];

    // Branching crystal structures
    const branches = [
      { startX: 100, startY: 250, length: 60, angle: -30 },
      { startX: 400, startY: 250, length: 80, angle: 30 },
      { startX: 700, startY: 250, length: 70, angle: -20 },
      { startX: 1000, startY: 250, length: 65, angle: 25 },
    ];

    branches.forEach((branch) => {
      const endX =
        branch.startX +
        branch.length * Math.cos((branch.angle * Math.PI) / 180);
      const endY =
        branch.startY +
        branch.length * Math.sin((branch.angle * Math.PI) / 180);

      patterns.push(`
        <line x1='${branch.startX}' y1='${branch.startY}' 
              x2='${endX}' y2='${endY}' 
              stroke='#c7cbe6' stroke-width='1.8' opacity='0.3'/>
        
        <!-- Sub-branches -->
        <line x1='${(branch.startX + endX) / 2}' y1='${
        (branch.startY + endY) / 2
      }' 
              x2='${(branch.startX + endX) / 2 + 20}' y2='${
        (branch.startY + endY) / 2 - 15
      }' 
              stroke='#c7cbe6' stroke-width='1.2' opacity='0.2'/>
              
        <line x1='${(branch.startX + endX) / 2}' y1='${
        (branch.startY + endY) / 2
      }' 
              x2='${(branch.startX + endX) / 2 - 15}' y2='${
        (branch.startY + endY) / 2 + 20
      }' 
              stroke='#c7cbe6' stroke-width='1.2' opacity='0.2'/>
      `);
    });

    return patterns.join("");
  };

  return encodeURIComponent(`
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300' preserveAspectRatio='none'>
      <defs>
        <!-- Enhanced gradient background -->
        <linearGradient id='grad-bg' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stop-color='#4a5599' />
          <stop offset='25%' stop-color='#5c67aa' />
          <stop offset='50%' stop-color='#6d76b5' />
          <stop offset='75%' stop-color='#b7bce3' />
          <stop offset='100%' stop-color='#ffffff' />
        </linearGradient>

        <!-- Crystal highlight effect -->
        <radialGradient id='crystal-glow'>
          <stop offset='0%' stop-color='white' stop-opacity='0.4'/>
          <stop offset='100%' stop-color='white' stop-opacity='0'/>
        </radialGradient>

        <!-- Subtle grid pattern -->
        <pattern id='micro-grid' width='20' height='20' patternUnits='userSpaceOnUse'>
          <path d='M 20 0 L 0 0 0 20' fill='none' stroke='white' stroke-opacity='0.03' stroke-width='0.5'/>
        </pattern>
      </defs>

      <!-- Background gradient -->
      <rect width='100%' height='100%' fill='url(#grad-bg)'/>
      
      <!-- Micro grid pattern -->
      <rect width='100%' height='100%' fill='url(#micro-grid)'/>
      
      <!-- Crystal glow effects -->
      <ellipse cx='300' cy='50' rx='150' ry='40' fill='url(#crystal-glow)'/>
      <ellipse cx='900' cy='70' rx='120' ry='30' fill='url(#crystal-glow)'/>

      <!-- Atomic lattice foundation -->
      <g>${createAtomicLattice()}</g>

      <!-- Main quartz crystal structures -->
      <g>${createQuartzLattice()}</g>

      <!-- Crystal growth patterns -->
      <g>${createCrystalGrowth()}</g>

      <!-- Refraction lines -->
      <g stroke='white' stroke-opacity='0.1' stroke-width='0.8'>
        <path d='M50 40 Q200 80 350 40' fill='none'/>
        <path d='M850 60 Q1000 100 1150 60' fill='none'/>
      </g>
    </svg>
  `);
};

// Export pre-computed SVG for performance
export const CRYSTAL_BACKGROUND_SVG = createCrystalBackgroundSVG();